import React from "react";
import { AlertTriangle } from "lucide-react";

// Shared compact equipment card for the GR system pages (Hydrant, Ventilation): a large,
// status-colored equipment icon on the left so state reads at a glance, kept short so a whole
// system fits on screen without scrolling.

// /assets/live-values returns each register's last known value with its real last-seen time,
// even when the device behind it has gone quiet — same stale rule as Live Monitor.
export const STALE_AFTER_MS = 5 * 60 * 1000;

export function fmtAge(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "";
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m / 60)}h ago`;
  return `${Math.floor(m / 1440)}d ago`;
}

// Status → color of the equipment icon badge on each card, so state reads at a glance.
export const TONE = {
  good:    "bg-emerald-50 border-emerald-200 text-emerald-600",
  idle:    "bg-slate-50 border-slate-200 text-slate-500",
  offline: "bg-gray-100 border-gray-200 text-gray-400",
  bad:     "bg-red-50 border-red-200 text-red-600",
  warn:    "bg-amber-50 border-amber-200 text-amber-600",
};

// Compact card with a large, status-colored equipment icon on the left. Kept short so a whole
// system fits on screen without scrolling. A stale reading gets an amber ring and a
// "Stale · Xh ago" pill on the card edge.
export function EquipmentCard({ title, group, icon: Icon, iconClassName = "", tone = "idle", pulse, live, children, footer, testid }) {
  const stale = live?.ingested_at && Date.now() - new Date(live.ingested_at).getTime() > STALE_AFTER_MS;
  return (
    <div className={`glass p-3 rounded-lg flex flex-col min-w-0 relative ${stale ? "ring-2 ring-amber-300" : ""}`} data-testid={testid}>
      {stale && (
        <span
          className="absolute -top-2 right-2 z-10 inline-flex items-center gap-1 px-1.5 py-px rounded-full bg-amber-50 border border-amber-200 text-amber-700 text-[9px] font-bold uppercase tracking-wider"
          title={`Last reading ${new Date(live.ingested_at).toLocaleString("en-IN")} — no newer data from this device`}
        >
          <AlertTriangle className="w-3 h-3" strokeWidth={2.5} /> Stale · {fmtAge(live.ingested_at)}
        </span>
      )}
      <div className="flex items-center gap-3 min-w-0">
        <div className={`shrink-0 w-12 h-12 rounded-xl border flex items-center justify-center ${TONE[tone]} ${pulse ? "status-pulse-green" : ""}`}>
          <Icon className={`w-7 h-7 ${iconClassName}`} strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[9px] uppercase tracking-widest text-orange-500 font-bold truncate">{group}</div>
          <h3 className="text-xs uppercase tracking-wider text-gray-700 font-bold truncate" title={title}>{title}</h3>
          {children}
        </div>
      </div>
      {footer}
    </div>
  );
}

export function Stat({ label, children, className = "" }) {
  return (
    <div className="bg-gray-50 rounded px-2 py-1 min-w-0">
      <div className="text-gray-500 uppercase text-[9px] tracking-wider">{label}</div>
      <div className={`font-semibold text-[11px] truncate ${className || "text-gray-900"}`}>{children}</div>
    </div>
  );
}
